import React, { useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { get } from 'lodash'
import moment from 'moment'
import { Button, DialogActions } from '@material-ui/core'
import {
  RecurrenceTypes,
  FieldNames,
  IPublishScheduleFormErrors,
  IFormFieldsValidated,
  IScheduleRecord,
} from '../../types/publishSchedule'
import { ICustomHTTPError } from '../../types/http'
import {
  scheduleModalClose,
  setFormErrors,
  clearFormErrors,
  scheduleRequestStart,
  scheduleRequestFinish,
} from '../../actions/publishSchedule'
import { isEmptyObject, trace } from '../../services/common'
import { getMinEndDate } from '../../services/publishSchedule'
import {
  createSchedule,
  updateSchedule,
  validationErrorReducer,
} from '../../services/publishSchedule'
import { messages } from '../../config/messages'
import { scheduleValidationSchema } from './validation'
import { useCustomSnackbar } from '../CustomSnackbar/hook'
import { usePublishSchedule, useFetchSchedules } from './hooks'
import { DateTime, Recurrence, Period, IntradayRepeat, UTC } from './FormFields'
import { StyledModalContainer, StyledModalContent } from './styled'

export const AddPublishScheduleForm = () => {
  const { groupId, packId } = useParams()
  const [state, dispatch] = usePublishSchedule()
  const fetchSchedules = useFetchSchedules()
  const [, setSnackbarMessage] = useCustomSnackbar()
  const daysInSelectedMonth = useMemo(
    (): number =>
      moment(state.fieldValues.startTime || new Date())
        .month(state.fieldValues.recurrenceMonth - 1)
        .daysInMonth(),
    [state.fieldValues.recurrenceMonth, state.fieldValues.startTime]
  )

  const isUpdating = !!state.currentScheduleId
  const validateForm = useCallback((): IFormFieldsValidated | undefined => {
    try {
      const validatedFields = scheduleValidationSchema.validateSync(
        state.fieldValues,
        {
          abortEarly: false,
          context: {
            currentDate: new Date(),
            minEndDate: getMinEndDate(state.fieldValues),
            daysInSelectedMonth,
            isUpdating,
          },
        }
      )
      dispatch(clearFormErrors())
      return (validatedFields as any) as IFormFieldsValidated
    } catch (validationError) {
      const errors: IPublishScheduleFormErrors = get(
        validationError,
        'inner',
        []
      ).reduce(validationErrorReducer, {})
      dispatch(setFormErrors(errors))
      return
    }
  }, [dispatch, state.fieldValues, daysInSelectedMonth, isUpdating])

  useEffect(() => {
    validateForm()
  }, [validateForm])

  const handleSaveSuccess = () => {
    setSnackbarMessage(
      isUpdating
        ? messages.scheduleUpdateSuccess
        : messages.scheduleCreateSuccess
    )
    fetchSchedules()
    dispatch(scheduleRequestFinish())
    dispatch(scheduleModalClose())
  }

  const handleSaveError = (err: ICustomHTTPError) => {
    trace(err)
    setSnackbarMessage(err.message)
    dispatch(scheduleRequestFinish())
  }

  const handleSave = async (): Promise<void> => {
    const formFieldsData = validateForm()
    if (!!formFieldsData) {
      try {
        dispatch(scheduleRequestStart())
        if (isUpdating) {
          const previousScheduleData = state.schedules.find(
            ({ scheduleId }) => scheduleId === state.currentScheduleId
          )
          await updateSchedule(
            previousScheduleData as IScheduleRecord,
            formFieldsData,
            packId
          )
        } else {
          await createSchedule(formFieldsData, groupId, packId)
        }
        handleSaveSuccess()
      } catch (err) {
        handleSaveError(err)
      }
    }
  }

  return (
    <StyledModalContainer>
      <StyledModalContent>
        <DateTime fieldName={FieldNames.startTime} />
        <Recurrence />
        {state.fieldValues.recurrence !== RecurrenceTypes.none && <Period />}
        <IntradayRepeat />
        <UTC />
      </StyledModalContent>
      <DialogActions>
        <Button
          id="cancel-button"
          type="button"
          className="cancel-button"
          onClick={() => {
            dispatch(scheduleModalClose())
          }}
          size="small"
          autoFocus
        >
          Cancel
        </Button>
        <Button
          id="action-button"
          type="button"
          color="primary"
          className="action-button"
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={!isEmptyObject(state.fieldErrors)}
        >
          Save
        </Button>
      </DialogActions>
    </StyledModalContainer>
  )
}
